import ThemedSelect from '../../common/ThemedSelect';
import UnifiedDateRangePicker from '../../common/UnifiedDateRangePicker';
import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  FileSpreadsheet,
  BarChart2,
  ClipboardList,
  Scale,
  AlertCircle,
  ChevronRight,
  Download,
  Loader2,
  Calendar,
  CheckCircle,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { formatAppDate } from '../../../utils/dateUtils';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import ExcelJS from 'exceljs';
import api from '../../../services/weighbridgeComplianceApi';

// ============================================================================
// Constants
// ============================================================================

const TEAL = '#45B7AA';
const TEAL_DARK = '#369d91';
const TEAL_LIGHT = '#e6f7f6';

const REPORT_TYPES = [
  {
    id: 'full_compliance',
    label: 'Full Compliance Report',
    description: 'All assets, calibrations, and maintenance status in one report.',
    icon: Scale,
    color: 'teal',
  },
  {
    id: 'asset_detail',
    label: 'Asset Detail Report',
    description: 'Single asset with full calibration and maintenance history.',
    icon: ClipboardList,
    color: 'blue',
    requiresAsset: true,
  },
  {
    id: 'calibration_status',
    label: 'Calibration Status Report',
    description: 'All certificates with expiry dates and compliance status.',
    icon: CheckCircle,
    color: 'emerald',
  },
  {
    id: 'maintenance_summary',
    label: 'Maintenance Summary',
    description: 'Overview of completed and overdue maintenance tasks.',
    icon: BarChart2,
    color: 'amber',
  },
  {
    id: 'ncr_summary',
    label: 'NCR Summary',
    description: 'Open and closed Non-Conformance Reports with status.',
    icon: AlertCircle,
    color: 'red',
  },
];

const CARD_COLOR_MAP = {
  teal: {
    border: 'border-teal-200',
    bg: 'bg-teal-50/60',
    iconBg: 'bg-teal-500',
    selectedBorder: 'border-teal-400',
    selectedBg: 'bg-teal-50',
  },
  blue: {
    border: 'border-blue-200',
    bg: 'bg-blue-50/60',
    iconBg: 'bg-blue-500',
    selectedBorder: 'border-blue-400',
    selectedBg: 'bg-blue-50',
  },
  emerald: {
    border: 'border-emerald-200',
    bg: 'bg-emerald-50/60',
    iconBg: 'bg-emerald-500',
    selectedBorder: 'border-emerald-400',
    selectedBg: 'bg-emerald-50',
  },
  amber: {
    border: 'border-amber-200',
    bg: 'bg-amber-50/60',
    iconBg: 'bg-amber-500',
    selectedBorder: 'border-amber-400',
    selectedBg: 'bg-amber-50',
  },
  red: {
    border: 'border-red-200',
    bg: 'bg-red-50/60',
    iconBg: 'bg-red-500',
    selectedBorder: 'border-red-400',
    selectedBg: 'bg-red-50',
  },
};

// ============================================================================
// Helpers
// ============================================================================

function safeFormat(dateVal, fmt) {
  if (!dateVal) return '—';
  if (fmt) {
    // Custom format requested (e.g. 'dd MMM yyyy') — use date-fns format
    try {
      const d = typeof dateVal === 'string' ? new Date(dateVal) : new Date(dateVal);
      return isNaN(d.getTime()) ? '—' : format(d, fmt);
    } catch {
      return '—';
    }
  }
  return formatAppDate(dateVal) || '—';
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 100);
}

function statusColor(status) {
  const s = (status || '').toLowerCase();
  if (s === 'compliant' || s === 'completed' || s === 'closed' || s === 'pass') return '#16a34a';
  if (s === 'expiring' || s === 'due' || s === 'scheduled') return '#d97706';
  if (s === 'expired' || s === 'overdue' || s === 'open' || s === 'fail' || s === 'non-compliant')
    return '#dc2626';
  return '#64748b';
}

function hexToArgb(hex) {
  // ExcelJS uses ARGB format: FF + hex without #
  return 'FF' + hex.replace('#', '').toUpperCase();
}

// ============================================================================
// PDF generation helpers
// ============================================================================

function addPdfHeader(doc, title, dateRange) {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Teal header bar
  doc.setFillColor(69, 183, 170); // #45B7AA
  doc.rect(0, 0, pageWidth, 28, 'F');

  // Logo text
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(255, 255, 255);
  doc.text('LexiBridge', 14, 12);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Weighbridge Compliance System', 14, 20);

  // Report title (right-aligned)
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text(title, pageWidth - 14, 12, { align: 'right' });

  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  const generatedText = `Generated: ${format(new Date(), 'dd MMM yyyy HH:mm')}`;
  doc.text(generatedText, pageWidth - 14, 20, { align: 'right' });

  // Date range line
  if (dateRange) {
    doc.setTextColor(100, 116, 139);
    doc.setFontSize(8);
    doc.text(
      `Period: ${dateRange.from ? safeFormat(dateRange.from) : 'All time'} — ${dateRange.to ? safeFormat(dateRange.to) : 'Present'}`,
      14,
      35
    );
    return 42; // y position after header
  }
  return 38;
}

function addPdfFooter(doc) {
  const pageCount = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFillColor(241, 245, 249); // slate-100
    doc.rect(0, pageHeight - 12, pageWidth, 12, 'F');
    doc.setFontSize(7);
    doc.setTextColor(100, 116, 139);
    doc.text('LexiBridge Weighbridge Compliance — Confidential', 14, pageHeight - 4);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - 14, pageHeight - 4, { align: 'right' });
  }
}

function addKpiSection(doc, stats, startY) {
  if (!stats) return startY;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text('Compliance Overview', 14, startY);

  const kpis = [
    { label: 'Total Assets', value: stats.total_assets ?? '—' },
    { label: 'Compliant', value: stats.compliant ?? '—' },
    { label: 'Expiring Soon', value: stats.expiring_soon ?? '—' },
    { label: 'Expired/Non-Compliant', value: stats.non_compliant ?? '—' },
  ];

  const boxW = (doc.internal.pageSize.getWidth() - 28) / 4;
  const boxH = 20;
  let x = 14;
  let y = startY + 4;

  kpis.forEach((kpi, idx) => {
    if (idx > 0 && idx % 4 === 0) {
      x = 14;
      y += boxH + 4;
    }
    doc.setFillColor(230, 247, 246);
    doc.setDrawColor(69, 183, 170);
    doc.roundedRect(x, y, boxW - 2, boxH, 2, 2, 'FD');

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    doc.text(kpi.label.toUpperCase(), x + 4, y + 6);

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 41, 59);
    doc.text(String(kpi.value), x + 4, y + 15);

    x += boxW;
  });

  return y + boxH + 8;
}

function buildPdfTable(doc, columns, rows, startY, statusCol = null) {
  doc.autoTable({
    head: [columns],
    body: rows,
    startY,
    margin: { left: 14, right: 14 },
    headStyles: {
      fillColor: [69, 183, 170],
      textColor: [255, 255, 255],
      fontSize: 8,
      fontStyle: 'bold',
      halign: 'left',
    },
    bodyStyles: {
      fontSize: 7.5,
      textColor: [30, 41, 59],
    },
    alternateRowStyles: {
      fillColor: [248, 250, 252],
    },
    columnStyles: statusCol !== null ? { [statusCol]: { fontStyle: 'bold' } } : {},
    didParseCell(data) {
      if (statusCol !== null && data.column.index === statusCol && data.section === 'body') {
        const val = data.cell.raw;
        const c = statusColor(val);
        // Convert hex to RGB
        const r = parseInt(c.slice(1, 3), 16);
        const g = parseInt(c.slice(3, 5), 16);
        const b = parseInt(c.slice(5, 7), 16);
        data.cell.styles.textColor = [r, g, b];
      }
    },
    tableLineColor: [226, 232, 240],
    tableLineWidth: 0.1,
  });

  return doc.lastAutoTable.finalY + 6;
}

// ============================================================================
// Excel generation helpers
// ============================================================================

function styleExcelHeader(worksheet, row, colCount) {
  const headerRow = worksheet.getRow(row);
  headerRow.height = 22;
  for (let c = 1; c <= colCount; c++) {
    const cell = headerRow.getCell(c);
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: hexToArgb(TEAL) },
    };
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10, name: 'Calibri' };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
    cell.border = {
      bottom: { style: 'thin', color: { argb: hexToArgb(TEAL_DARK) } },
    };
  }
}

function styleExcelDataRow(worksheet, rowNum, colCount, isAlt) {
  const row = worksheet.getRow(rowNum);
  row.height = 16;
  for (let c = 1; c <= colCount; c++) {
    const cell = row.getCell(c);
    if (isAlt) {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFF8FFFE' },
      };
    }
    cell.font = { size: 9, name: 'Calibri' };
    cell.alignment = { vertical: 'middle' };
  }
}

function applyStatusColor(cell, value) {
  const c = statusColor(value);
  cell.font = { bold: true, color: { argb: hexToArgb(c) }, size: 9, name: 'Calibri' };
}

function autoFitColumns(worksheet) {
  worksheet.columns.forEach((col) => {
    let maxLen = 10;
    col.eachCell({ includeEmpty: false }, (cell) => {
      const len = cell.value ? String(cell.value).length : 0;
      if (len > maxLen) maxLen = len;
    });
    col.width = Math.min(maxLen + 4, 50);
  });
}

function addExcelSheet(workbook, sheetName, columns, rows, statusColIndex = null) {
  const ws = workbook.addWorksheet(sheetName);

  // Title row
  ws.mergeCells(1, 1, 1, columns.length);
  const titleCell = ws.getCell(1, 1);
  titleCell.value = `LexiBridge Weighbridge Compliance — ${sheetName}`;
  titleCell.font = { bold: true, size: 12, name: 'Calibri', color: { argb: hexToArgb(TEAL_DARK) } };
  titleCell.alignment = { horizontal: 'left', vertical: 'middle' };
  ws.getRow(1).height = 26;

  // Generated line
  ws.mergeCells(2, 1, 2, columns.length);
  const genCell = ws.getCell(2, 1);
  genCell.value = `Generated: ${format(new Date(), 'dd MMM yyyy HH:mm')}`;
  genCell.font = { italic: true, size: 8, name: 'Calibri', color: { argb: 'FF64748B' } };
  ws.getRow(2).height = 14;

  // Header row (row 3)
  const headerRow = ws.getRow(3);
  columns.forEach((col, i) => {
    headerRow.getCell(i + 1).value = col;
  });
  styleExcelHeader(ws, 3, columns.length);

  // Data rows
  rows.forEach((row, rowIdx) => {
    const wsRow = ws.getRow(rowIdx + 4);
    row.forEach((val, colIdx) => {
      const cell = wsRow.getCell(colIdx + 1);
      cell.value = val;
      if (statusColIndex !== null && colIdx === statusColIndex) {
        applyStatusColor(cell, val);
      }
    });
    styleExcelDataRow(ws, rowIdx + 4, columns.length, rowIdx % 2 === 1);
  });

  autoFitColumns(ws);
  return ws;
}

// ============================================================================
// Report builders
// ============================================================================

async function buildFullCompliancePdf(data, dateRange) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  let y = addPdfHeader(doc, 'Full Compliance Report', dateRange);
  y = addKpiSection(doc, data.stats, y);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text('Asset Register', 14, y);
  y += 4;

  y = buildPdfTable(
    doc,
    [
      'Asset Name',
      'Location',
      'Installation Type',
      'Status',
      'Last Calibration',
      'Next Due',
      'Max Capacity (kg)',
    ],
    (data.assets || []).map((a) => [
      a.name || '—',
      a.location_name || '—',
      a.installation_type || '—',
      a.status || '—',
      safeFormat(a.last_cert_date),
      safeFormat(a.next_cert_due_date),
      a.capacity_max ?? '—',
    ]),
    y,
    3
  );

  if (doc.lastAutoTable && doc.lastAutoTable.finalY + 20 > doc.internal.pageSize.getHeight() - 20) {
    doc.addPage();
    y = 20;
  }

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text('Calibration Certificates', 14, y);
  y += 4;

  y = buildPdfTable(
    doc,
    [
      'Asset',
      'Certificate Type',
      'Cert Number',
      'Issue Date',
      'Expiry Date',
      'Status',
      'Lab / Entity',
    ],
    (data.calibrations || []).map((c) => [
      c.asset_name || '—',
      c.certificate_type || c.calibration_type || '—',
      c.certificate_number || '—',
      safeFormat(c.issue_date || c.calibration_date),
      safeFormat(c.next_due_date),
      c.status || '—',
      c.calibrating_entity || c.calibrating_entity || '—',
    ]),
    y,
    5
  );

  addPdfFooter(doc);
  return doc;
}

async function buildAssetDetailPdf(data, dateRange, assetName) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  let y = addPdfHeader(doc, `Asset Detail: ${assetName || 'Unknown'}`, dateRange);

  // Asset info block
  const asset = data.asset;
  if (asset) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(71, 85, 105);
    const infoLines = [
      `Location: ${asset.location_name || '—'}`,
      `Installation Type: ${asset.installation_type || '—'}`,
      `Max Capacity: ${asset.capacity_max ? asset.capacity_max + ' kg' : '—'}`,
      `Status: ${asset.status || '—'}`,
    ];
    infoLines.forEach((line) => {
      doc.text(line, 14, y);
      y += 5;
    });
    y += 3;
  }

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text('Calibration History', 14, y);
  y += 4;

  y = buildPdfTable(
    doc,
    ['Certificate Type', 'Cert Number', 'Issue Date', 'Expiry Date', 'Status', 'Lab'],
    (data.calibrations || []).map((c) => [
      c.certificate_type || c.calibration_type || '—',
      c.certificate_number || '—',
      safeFormat(c.issue_date || c.calibration_date),
      safeFormat(c.next_due_date),
      c.status || '—',
      c.calibrating_entity || '—',
    ]),
    y,
    4
  );

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text('Maintenance History', 14, y);
  y += 4;

  buildPdfTable(
    doc,
    ['Type', 'Scheduled Date', 'Completed Date', 'Status', 'Performed By', 'Findings'],
    (data.maintenance || []).map((m) => [
      m.maintenance_type || '—',
      safeFormat(m.scheduled_date),
      safeFormat(m.completed_date),
      m.status || '—',
      m.performed_by || '—',
      m.findings || '—',
    ]),
    y,
    3
  );

  addPdfFooter(doc);
  return doc;
}

async function buildCalibrationStatusPdf(data, dateRange) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  let y = addPdfHeader(doc, 'Calibration Status Report', dateRange);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 41, 59);
  doc.text('All Calibration Certificates', 14, y);
  y += 4;

  buildPdfTable(
    doc,
    [
      'Asset',
      'Certificate Type',
      'Cert Number',
      'Issue Date',
      'Expiry Date',
      'Status',
      'Lab / Entity',
      'Verification Officer',
    ],
    (data.calibrations || []).map((c) => [
      c.asset_name || '—',
      c.certificate_type || c.calibration_type || '—',
      c.certificate_number || '—',
      safeFormat(c.issue_date || c.calibration_date),
      safeFormat(c.next_due_date),
      c.status || '—',
      c.calibrating_entity || c.calibrating_entity || '—',
      c.verification_officer || '—',
    ]),
    y,
    5
  );

  addPdfFooter(doc);
  return doc;
}

async function buildMaintenanceSummaryPdf(data, dateRange) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  let y = addPdfHeader(doc, 'Maintenance Summary', dateRange);

  // Mini KPIs
  const completed = (data.maintenance || []).filter(
    (m) => (m.status || '').toLowerCase() === 'completed'
  ).length;
  const overdue = (data.maintenance || []).filter(
    (m) => (m.status || '').toLowerCase() === 'overdue'
  ).length;
  const scheduled = (data.maintenance || []).filter(
    (m) => (m.status || '').toLowerCase() === 'scheduled'
  ).length;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(71, 85, 105);
  doc.text(
    `Total: ${(data.maintenance || []).length}   Completed: ${completed}   Overdue: ${overdue}   Scheduled: ${scheduled}`,
    14,
    y
  );
  y += 8;

  buildPdfTable(
    doc,
    ['Asset', 'Type', 'Scheduled Date', 'Completed Date', 'Status', 'Performed By', 'Findings'],
    (data.maintenance || []).map((m) => [
      m.asset_name || '—',
      m.maintenance_type || '—',
      safeFormat(m.scheduled_date),
      safeFormat(m.completed_date),
      m.status || '—',
      m.performed_by || '—',
      m.findings || '',
    ]),
    y,
    4
  );

  addPdfFooter(doc);
  return doc;
}

async function buildNcrSummaryPdf(data, dateRange) {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  let y = addPdfHeader(doc, 'NCR Summary', dateRange);

  const open = (data.ncrs || []).filter((n) => (n.status || '').toLowerCase() === 'open').length;
  const closed = (data.ncrs || []).filter(
    (n) => (n.status || '').toLowerCase() === 'closed'
  ).length;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(71, 85, 105);
  doc.text(`Total NCRs: ${(data.ncrs || []).length}   Open: ${open}   Closed: ${closed}`, 14, y);
  y += 8;

  buildPdfTable(
    doc,
    [
      'NCR Number',
      'Asset',
      'Category',
      'Description',
      'Created',
      'Due Date',
      'Status',
      'Assigned To',
    ],
    (data.ncrs || []).map((n) => [
      n.ncr_number || n.id || '—',
      n.asset_name || '—',
      n.category || '—',
      n.description || '—',
      safeFormat(n.created_at),
      safeFormat(n.due_date),
      n.status || '—',
      n.assigned_to || '—',
    ]),
    y,
    6
  );

  addPdfFooter(doc);
  return doc;
}

// ============================================================================
// Excel builders
// ============================================================================

async function buildFullComplianceExcel(data) {
  const wb = new ExcelJS.Workbook();
  wb.creator = 'LexiBridge';
  wb.created = new Date();

  addExcelSheet(
    wb,
    'Asset Register',
    [
      'Asset Name',
      'Location',
      'Installation Type',
      'Status',
      'Last Calibration',
      'Next Due',
      'Max Capacity (kg)',
    ],
    (data.assets || []).map((a) => [
      a.name || '',
      a.location_name || '',
      a.installation_type || '',
      a.status || '',
      safeFormat(a.last_cert_date),
      safeFormat(a.next_cert_due_date),
      a.capacity_max ?? '',
    ]),
    3
  );

  addExcelSheet(
    wb,
    'Calibrations',
    [
      'Asset',
      'Certificate Type',
      'Cert Number',
      'Issue Date',
      'Expiry Date',
      'Status',
      'Lab / Entity',
    ],
    (data.calibrations || []).map((c) => [
      c.asset_name || '',
      c.certificate_type || c.calibration_type || '',
      c.certificate_number || '',
      safeFormat(c.issue_date || c.calibration_date),
      safeFormat(c.next_due_date),
      c.status || '',
      c.calibrating_entity || c.calibrating_entity || '',
    ]),
    5
  );

  addExcelSheet(
    wb,
    'Maintenance',
    ['Asset', 'Type', 'Scheduled Date', 'Completed Date', 'Status', 'Performed By'],
    (data.maintenance || []).map((m) => [
      m.asset_name || '',
      m.maintenance_type || '',
      safeFormat(m.scheduled_date),
      safeFormat(m.completed_date),
      m.status || '',
      m.performed_by || '',
    ]),
    4
  );

  addExcelSheet(
    wb,
    'NCRs',
    ['NCR Number', 'Asset', 'Category', 'Description', 'Created', 'Due Date', 'Status'],
    (data.ncrs || []).map((n) => [
      n.ncr_number || n.id || '',
      n.asset_name || '',
      n.category || '',
      n.description || '',
      safeFormat(n.created_at),
      safeFormat(n.due_date),
      n.status || '',
    ]),
    6
  );

  return wb;
}

// ============================================================================
// Main component
// ============================================================================

const ComplianceReports = () => {
  const [selectedType, setSelectedType] = useState(null);
  const [selectedAssetId, setSelectedAssetId] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [format_, setFormat] = useState('pdf'); // 'pdf' | 'excel'
  const [loading, setLoading] = useState(false);
  const [assets, setAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);

  // Load asset list for the asset-detail dropdown
  useEffect(() => {
    if (selectedType?.requiresAsset && assets.length === 0) {
      setAssetsLoading(true);
      api.assets
        .getAll()
        .then((res) => setAssets(Array.isArray(res.data) ? res.data : res.data?.data || []))
        .catch(() => toast.error('Failed to load asset list.'))
        .finally(() => setAssetsLoading(false));
    }
  }, [selectedType, assets.length]);

  const handleSelectType = useCallback((type) => {
    setSelectedType(type);
    setSelectedAssetId('');
  }, []);

  const handleGenerate = useCallback(async () => {
    if (!selectedType) return;
    if (selectedType.requiresAsset && !selectedAssetId) {
      toast.error('Please select an asset.');
      return;
    }

    setLoading(true);
    try {
      const dateRange = {
        from: dateFrom || null,
        to: dateTo || null,
      };

      const params = {};
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;

      let data = {};

      // Helper to extract array from paginated responses ({ data: [...], total, ... })
      const extractList = (res) => {
        const d = res.data;
        return Array.isArray(d) ? d : d?.data || [];
      };

      if (selectedType.id === 'full_compliance') {
        const [statsRes, assetsRes, calsRes, maintRes, ncrRes] = await Promise.all([
          api.assets.getDashboardStats(),
          api.assets.getAll(params),
          api.calibrations.getAll(params),
          api.maintenance.getAll(params),
          api.ncr.getAll(params),
        ]);
        data = {
          stats: statsRes.data,
          assets: extractList(assetsRes),
          calibrations: extractList(calsRes),
          maintenance: extractList(maintRes),
          ncrs: extractList(ncrRes),
        };
      } else if (selectedType.id === 'asset_detail') {
        const [assetRes, calsRes, maintRes] = await Promise.all([
          api.assets.get(selectedAssetId),
          api.calibrations.getByAsset(selectedAssetId),
          api.maintenance.getByAsset(selectedAssetId),
        ]);
        data = {
          asset: assetRes.data?.asset || assetRes.data,
          calibrations: extractList(calsRes),
          maintenance: extractList(maintRes),
        };
      } else if (selectedType.id === 'calibration_status') {
        const res = await api.calibrations.getAll(params);
        data = { calibrations: extractList(res) };
      } else if (selectedType.id === 'maintenance_summary') {
        const res = await api.maintenance.getAll(params);
        data = { maintenance: extractList(res) };
      } else if (selectedType.id === 'ncr_summary') {
        const res = await api.ncr.getAll(params);
        data = { ncrs: extractList(res) };
      }

      const assetName =
        selectedType.requiresAsset && selectedAssetId
          ? assets.find((a) => String(a.id) === String(selectedAssetId))?.name || 'Asset'
          : null;

      const timestamp = format(new Date(), 'yyyyMMdd_HHmm');

      if (format_ === 'pdf') {
        let doc;
        let filename;

        if (selectedType.id === 'full_compliance') {
          doc = await buildFullCompliancePdf(data, dateRange);
          filename = `LexiBridge_FullCompliance_${timestamp}.pdf`;
        } else if (selectedType.id === 'asset_detail') {
          doc = await buildAssetDetailPdf(data, dateRange, assetName);
          filename = `LexiBridge_AssetDetail_${assetName?.replace(/\s+/g, '_') || 'Unknown'}_${timestamp}.pdf`;
        } else if (selectedType.id === 'calibration_status') {
          doc = await buildCalibrationStatusPdf(data, dateRange);
          filename = `LexiBridge_CalibrationStatus_${timestamp}.pdf`;
        } else if (selectedType.id === 'maintenance_summary') {
          doc = await buildMaintenanceSummaryPdf(data, dateRange);
          filename = `LexiBridge_MaintenanceSummary_${timestamp}.pdf`;
        } else if (selectedType.id === 'ncr_summary') {
          doc = await buildNcrSummaryPdf(data, dateRange);
          filename = `LexiBridge_NCR_Summary_${timestamp}.pdf`;
        }

        const blob = doc.output('blob');
        triggerDownload(blob, filename);
        toast.success(`${selectedType.label} downloaded as PDF.`);
      } else {
        // Excel
        const wb = new ExcelJS.Workbook();
        wb.creator = 'LexiBridge';
        wb.created = new Date();

        let filename;

        if (selectedType.id === 'full_compliance') {
          const fullWb = await buildFullComplianceExcel(data);
          filename = `LexiBridge_FullCompliance_${timestamp}.xlsx`;
          const buffer = await fullWb.xlsx.writeBuffer();
          triggerDownload(
            new Blob([buffer], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            }),
            filename
          );
        } else if (selectedType.id === 'asset_detail') {
          addExcelSheet(
            wb,
            'Calibrations',
            ['Certificate Type', 'Cert Number', 'Issue Date', 'Expiry Date', 'Status', 'Lab'],
            (data.calibrations || []).map((c) => [
              c.certificate_type || c.calibration_type || '',
              c.certificate_number || '',
              safeFormat(c.issue_date || c.calibration_date),
              safeFormat(c.next_due_date),
              c.status || '',
              c.calibrating_entity || '',
            ]),
            4
          );
          addExcelSheet(
            wb,
            'Maintenance',
            ['Type', 'Scheduled Date', 'Completed Date', 'Status', 'Performed By', 'Findings'],
            (data.maintenance || []).map((m) => [
              m.maintenance_type || '',
              safeFormat(m.scheduled_date),
              safeFormat(m.completed_date),
              m.status || '',
              m.performed_by || '',
              m.findings || '',
            ]),
            3
          );
          filename = `LexiBridge_AssetDetail_${assetName?.replace(/\s+/g, '_') || 'Unknown'}_${timestamp}.xlsx`;
          const buffer = await wb.xlsx.writeBuffer();
          triggerDownload(
            new Blob([buffer], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            }),
            filename
          );
        } else if (selectedType.id === 'calibration_status') {
          addExcelSheet(
            wb,
            'Calibration Status',
            [
              'Asset',
              'Certificate Type',
              'Cert Number',
              'Issue Date',
              'Expiry Date',
              'Status',
              'Lab',
              'Officer',
            ],
            (data.calibrations || []).map((c) => [
              c.asset_name || '',
              c.certificate_type || c.calibration_type || '',
              c.certificate_number || '',
              safeFormat(c.issue_date || c.calibration_date),
              safeFormat(c.next_due_date),
              c.status || '',
              c.calibrating_entity || '',
              c.verification_officer || '',
            ]),
            5
          );
          filename = `LexiBridge_CalibrationStatus_${timestamp}.xlsx`;
          const buffer = await wb.xlsx.writeBuffer();
          triggerDownload(
            new Blob([buffer], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            }),
            filename
          );
        } else if (selectedType.id === 'maintenance_summary') {
          addExcelSheet(
            wb,
            'Maintenance Summary',
            [
              'Asset',
              'Type',
              'Scheduled Date',
              'Completed Date',
              'Status',
              'Performed By',
              'Findings',
            ],
            (data.maintenance || []).map((m) => [
              m.asset_name || '',
              m.maintenance_type || '',
              safeFormat(m.scheduled_date),
              safeFormat(m.completed_date),
              m.status || '',
              m.performed_by || '',
              m.findings || '',
            ]),
            4
          );
          filename = `LexiBridge_MaintenanceSummary_${timestamp}.xlsx`;
          const buffer = await wb.xlsx.writeBuffer();
          triggerDownload(
            new Blob([buffer], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            }),
            filename
          );
        } else if (selectedType.id === 'ncr_summary') {
          addExcelSheet(
            wb,
            'NCR Summary',
            [
              'NCR Number',
              'Asset',
              'Category',
              'Description',
              'Created',
              'Due Date',
              'Status',
              'Assigned To',
            ],
            (data.ncrs || []).map((n) => [
              n.ncr_number || n.id || '',
              n.asset_name || '',
              n.category || '',
              n.description || '',
              safeFormat(n.created_at),
              safeFormat(n.due_date),
              n.status || '',
              n.assigned_to || '',
            ]),
            6
          );
          filename = `LexiBridge_NCR_Summary_${timestamp}.xlsx`;
          const buffer = await wb.xlsx.writeBuffer();
          triggerDownload(
            new Blob([buffer], {
              type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            }),
            filename
          );
        }

        toast.success(`${selectedType.label} downloaded as Excel.`);
      }
    } catch (err) {
      console.error('Report generation error:', err);
      toast.error('Failed to generate report. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedType, selectedAssetId, dateFrom, dateTo, format_, assets]);

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div className="space-y-6">
      {/* Page heading */}
      <div>
        <h2
          className="text-xl font-bold text-slate-800"
          style={{ fontFamily: 'DM Sans, sans-serif' }}
        >
          Compliance Reports
        </h2>
        <p className="text-sm text-slate-500 mt-0.5">
          Generate PDF or Excel reports for compliance records, calibrations, maintenance, and NCRs.
        </p>
      </div>

      {/* Step 1: Report type selector */}
      <div className="rounded-xl border border-slate-200 bg-surface p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-700 mb-4 uppercase tracking-wider">
          1. Select Report Type
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {REPORT_TYPES.map((type) => {
            const colorMap = CARD_COLOR_MAP[type.color];
            const isSelected = selectedType?.id === type.id;
            const Icon = type.icon;

            return (
              <button
                key={type.id}
                onClick={() => handleSelectType(type)}
                className={`
                  relative flex items-start gap-3 rounded-xl border p-4 text-left transition-all
                  hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-1
                  ${
                    isSelected
                      ? `${colorMap.selectedBorder} ${colorMap.selectedBg} shadow-sm ring-2 ring-offset-1`
                      : `${colorMap.border} bg-surface hover:${colorMap.bg}`
                  }
                `}
                style={isSelected ? { '--tw-ring-color': TEAL } : {}}
              >
                <div
                  className={`flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-lg ${colorMap.iconBg}`}
                >
                  <Icon
                    className="w-4.5 h-4.5 text-white"
                    style={{ width: '18px', height: '18px' }}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 leading-snug">{type.label}</p>
                  <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                    {type.description}
                  </p>
                </div>
                {isSelected && (
                  <div
                    className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: TEAL }}
                  >
                    <CheckCircle className="w-3.5 h-3.5 text-white" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Step 2: Configuration */}
      {selectedType && (
        <div className="rounded-xl border border-slate-200 bg-surface p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-slate-700 mb-4 uppercase tracking-wider">
            2. Configure Report
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Asset selector — only for asset detail */}
            {selectedType.requiresAsset && (
              <div className="md:col-span-2">
                <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                  Asset
                </label>
                {assetsLoading ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading assets...
                  </div>
                ) : (
                  <ThemedSelect
                    value={selectedAssetId}
                    onChange={(e) => setSelectedAssetId(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:border-transparent transition"
                    style={{ '--tw-ring-color': TEAL }}
                  >
                    <option value="">Select an asset...</option>
                    {assets.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                        {a.location_name ? ` — ${a.location_name}` : ''}
                      </option>
                    ))}
                  </ThemedSelect>
                )}
              </div>
            )}

            {/* Date range */}
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  Date Range
                </span>
              </label>
              <UnifiedDateRangePicker
                startDate={dateFrom ? new Date(dateFrom + 'T00:00:00') : null}
                onStartDateChange={(date) => {
                  if (!date) {
                    setDateFrom('');
                    return;
                  }
                  const d = new Date(date);
                  setDateFrom(
                    d.getFullYear() +
                      '-' +
                      String(d.getMonth() + 1).padStart(2, '0') +
                      '-' +
                      String(d.getDate()).padStart(2, '0')
                  );
                }}
                endDate={dateTo ? new Date(dateTo + 'T00:00:00') : null}
                onEndDateChange={(date) => {
                  if (!date) {
                    setDateTo('');
                    return;
                  }
                  const d = new Date(date);
                  setDateTo(
                    d.getFullYear() +
                      '-' +
                      String(d.getMonth() + 1).padStart(2, '0') +
                      '-' +
                      String(d.getDate()).padStart(2, '0')
                  );
                }}
              />
            </div>

            {/* Format toggle */}
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">
                Output Format
              </label>
              <div className="flex gap-2">
                <button
                  onClick={() => setFormat('pdf')}
                  className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-all ${
                    format_ === 'pdf'
                      ? 'text-white shadow-sm border-transparent'
                      : 'border-slate-200 bg-surface text-slate-600 hover:bg-slate-50'
                  }`}
                  style={format_ === 'pdf' ? { backgroundColor: TEAL, borderColor: TEAL } : {}}
                >
                  <FileText className="w-4 h-4" />
                  PDF
                </button>
                <button
                  onClick={() => setFormat('excel')}
                  className={`flex items-center gap-2 rounded-lg border px-4 py-2.5 text-sm font-medium transition-all ${
                    format_ === 'excel'
                      ? 'text-white shadow-sm border-transparent'
                      : 'border-slate-200 bg-surface text-slate-600 hover:bg-slate-50'
                  }`}
                  style={format_ === 'excel' ? { backgroundColor: TEAL, borderColor: TEAL } : {}}
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Excel
                </button>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-slate-100 my-5" />

          {/* Summary preview + Generate button */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-start gap-2.5">
              <div
                className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-lg mt-0.5"
                style={{ backgroundColor: TEAL_LIGHT }}
              >
                <ChevronRight className="w-4 h-4" style={{ color: TEAL }} />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-700">{selectedType.label}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {format_ === 'pdf' ? 'PDF' : 'Excel'} export
                  {dateFrom || dateTo
                    ? ` · ${dateFrom ? safeFormat(dateFrom, 'dd MMM yyyy') : 'All time'} — ${dateTo ? safeFormat(dateTo, 'dd MMM yyyy') : 'present'}`
                    : ' · All dates'}
                  {selectedType.requiresAsset && selectedAssetId
                    ? ` · ${assets.find((a) => String(a.id) === String(selectedAssetId))?.name || 'Selected asset'}`
                    : ''}
                </p>
              </div>
            </div>

            <button
              onClick={handleGenerate}
              disabled={loading || (selectedType.requiresAsset && !selectedAssetId)}
              className="flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              style={{ backgroundColor: TEAL }}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Download className="w-4 h-4" />
                  Generate Report
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Empty state when nothing selected */}
      {!selectedType && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/50 py-14 px-6 text-center">
          <div
            className="flex items-center justify-center w-12 h-12 rounded-xl mb-3"
            style={{ backgroundColor: TEAL_LIGHT }}
          >
            <FileText className="w-6 h-6" style={{ color: TEAL }} />
          </div>
          <p className="text-sm font-semibold text-slate-600">
            Select a report type above to get started.
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Choose the report you need, configure the date range and format, then generate and
            download.
          </p>
        </div>
      )}
    </div>
  );
};

export default ComplianceReports;
